import { type MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';

import Loader from 'src/components/common/loader';
import PopupMausoleumBank from './components/popup-mausoleum-bank';

import MausoleumStore from 'src/stores/mausoleum-store';

import { BANK_ELEMENT_SELECTOR } from 'src/constants';
import { useLocalization } from 'src/contexts/localization-context';
import { getMausoleumPlanWithBanks } from 'src/utils';
import type { MausoleumPlanProps } from './mausoleum-plan.props';

import './mausoleum-plan.scss';

const MausoleumPlan = ({ className, height, mausoleum, onSelectBank }: MausoleumPlanProps) => {
  const { getLocalization } = useLocalization();
  const [store] = useState(() => new MausoleumStore());
  const containerRef = useRef<HTMLDivElement | null>(null);
  const { bankModal, banksWithPlans, isBanksLoading, loadBanks, resetSelectedBank, selectBank } =
    store;

  const mausoleumPlan = useMemo(
    () => getMausoleumPlanWithBanks(mausoleum.plan, banksWithPlans),
    [banksWithPlans, mausoleum.plan]
  );

  useEffect(() => {
    void loadBanks(mausoleum.id);
  }, [mausoleum.id, loadBanks]);

  const handlePlanClick = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      const target = event.target;

      if (!(target instanceof Element)) {
        return;
      }

      const bankElement = target.closest<SVGElement>(BANK_ELEMENT_SELECTOR);
      const bankId = bankElement?.dataset.bankId;
      const bank = banksWithPlans.find(({ bank }) => bank.id === bankId)?.bank;

      if (!bank) {
        return;
      }

      const containerRect = containerRef.current?.getBoundingClientRect();

      if (!containerRect) {
        return;
      }

      void selectBank(bank, {
        x: event.clientX - containerRect.left,
        y: event.clientY - containerRect.top,
      });
    },
    [banksWithPlans, selectBank]
  );

  return (
    <section
      className={classNames('mausoleum-plan', className)}
      ref={containerRef}
      aria-label={getLocalization('Mausoleum Plan')}
      style={{ height }}
    >
      {isBanksLoading ? (
        <Loader isDebounce={false} />
      ) : mausoleumPlan ? (
        <div
          className="mausoleum-plan__svg"
          dangerouslySetInnerHTML={{ __html: mausoleumPlan }}
          onClick={handlePlanClick}
        />
      ) : (
        <p className="mausoleum-plan__empty">{getLocalization('No Banks Found')}</p>
      )}

      {bankModal && (
        <PopupMausoleumBank
          bank={bankModal.bank}
          bankTypeLabel={bankModal.bankTypeLabel}
          isLoading={bankModal.isLoading}
          position={bankModal.position}
          statRows={bankModal.statRows}
          onClose={resetSelectedBank}
          onSelectBank={onSelectBank}
        />
      )}
    </section>
  );
};

export default observer(MausoleumPlan);
